/**
 * Polls /api/geo-config-revision and emits geo-scoped refresh events ONLY when
 * an admin change matches this visitor's session geo (country, province, city, barrio).
 */

import { useEffect, useRef } from "react";
import { isAdminShellPath } from "@/lib/geoConsentRoutes";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import {
  applyRevisionSeen,
  buildSessionScopeKeys,
  dispatchGeoRevisionEvent,
  fetchGeoConfigRevision,
  filterRevisionForSession,
  findBumpedSessionScopes,
  GEO_CONFIG_REVISION_LS_KEY,
  kindsFromScopes,
  readSeenRevisions,
  sessionShouldPollGeoRevision,
  type GeoRevisionEventDetail,
} from "@/lib/geoConfigRevision";

// Gap mínimo entre hits a /api/geo-config-revision. Antes había un setInterval de
// 3s que pegaba ~1200 req/hora POR visitante solo para detectar un cambio admin
// (raro) → spam. Ahora es event-driven (mount + volver a la pestaña), con este gap
// para no rafaguear ante focus/visibility repetidos.
const MIN_POLL_GAP_MS = 60_000;

function processRevisionPayload(
  serverScopes: Record<string, number>,
  sessionKeys: string[],
): GeoRevisionEventDetail | null {
  const lastSeen = readSeenRevisions();
  const bumped = findBumpedSessionScopes(sessionKeys, serverScopes, lastSeen);
  // Registrar TODAS las keys de la sesión (no solo las bumpeadas): inicializa
  // el baseline de la primera visita — el próximo poll compara contra esto en
  // vez de tratar la key como "nunca vista" para siempre.
  applyRevisionSeen(serverScopes, sessionKeys);
  if (bumped.length === 0) return null;
  return { bumpedScopes: bumped, kinds: kindsFromScopes(bumped) };
}

/**
 * Mount once near app root. Coordinates session-scoped refresh signals.
 */
export function useGeoScopedConfigRefresh(): void {
  const sessionKeysRef = useRef<string[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Admin shell no necesita poll de revisión geo — reduce ERR_HTTP2_PROTOCOL_ERROR
    // al competir con /api/admin/* en la misma conexión www.
    if (isAdminShellPath(window.location.pathname)) return;

    const cfg = getServerTemplateConfig();
    sessionKeysRef.current = buildSessionScopeKeys(cfg);
    if (!sessionShouldPollGeoRevision(cfg)) return;

    let cancelled = false;

    const poll = async () => {
      if (cancelled || document.visibilityState !== "visible") return;
      const payload = await fetchGeoConfigRevision();
      if (cancelled || !payload) return;
      const detail = processRevisionPayload(payload.scopes, sessionKeysRef.current);
      if (detail) dispatchGeoRevisionEvent(detail);
    };

    // Sin timer constante: chequear al montar y solo cuando el usuario vuelve a
    // la pestaña (visibility/focus), con gap mínimo. Evita el spam de /api/geo-config-revision.
    let lastPollAt = 0;
    const pollIfStale = () => {
      if (cancelled || document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastPollAt < MIN_POLL_GAP_MS) return;
      lastPollAt = now;
      void poll();
    };

    pollIfStale();

    const onVisibility = () => {
      if (document.visibilityState === "visible") pollIfStale();
    };
    const onFocus = () => pollIfStale();

    const onStorage = (e: StorageEvent) => {
      if (cancelled || e.key !== GEO_CONFIG_REVISION_LS_KEY || !e.newValue) return;
      try {
        const raw = JSON.parse(e.newValue) as GeoRevisionEventDetail;
        if (!raw?.bumpedScopes?.length) return;
        const filtered = filterRevisionForSession(raw, sessionKeysRef.current);
        if (!filtered) return;
        const now = Date.now();
        const scopes: Record<string, number> = {};
        for (const k of filtered.bumpedScopes) scopes[k] = now;
        applyRevisionSeen(scopes, filtered.bumpedScopes);
        dispatchGeoRevisionEvent(filtered);
      } catch {
        /* ignore malformed */
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
}